'use strict';

import {gsap} from 'gsap';
import {ScrollTrigger} from 'gsap/ScrollTrigger';
import {Observer} from 'gsap/Observer';
import {SplitText} from 'gsap/SplitText';

const $ = jQuery.noConflict();
$(function() {

	// Color values mirror $green-dark in src/scss/config/_variables.scss.
	// If that variable changes, update these constants too.
	const COLOR_FILLED = '#032c13';
	const COLOR_PALE = 'rgba(3,44,19,0.15)';

	// On desktop the fill stays tied to scroll position — nothing plays on its
	// own — but the section is pinned and wheel/trackpad input is fed into a
	// speed-limited scroll of our own, so the banner can't be crossed faster
	// than the headline can fill in. Same approach as the logo-reveal block.
	//
	// MAX_STEP is px per 60fps frame (scaled for other refresh rates below), so
	// 8 ≈ 480px/s. This is the hard ceiling — spinning the wheel faster cannot
	// move the section any quicker, it only fills the queue. MAX_QUEUE caps how
	// far ahead input may bank: a hard flick glides on for about that distance
	// instead of jumping the whole section. ENTRY_MOMENTUM (seconds of incoming
	// velocity) carries the gesture that entered the section into that glide, so
	// taking over doesn't feel like a wall.
	const MAX_STEP = 8;
	const MAX_QUEUE = 450;
	const ENTRY_MOMENTUM = 0.12;

	// Share of the pinned scroll spent filling. The remainder is an idle hold
	// that keeps the finished headline on screen for a beat before it unpins.
	const FILL_RATIO = 0.8;

	// Below this width the banner is shorter than the fold, so pinning it parks a
	// band of empty page under the headline until the next block arrives. Tablets
	// get the self-playing reveal instead. Matches $xl in _variables.scss, where
	// the fluid type and padding stop scaling.
	const PIN_MIN_WIDTH = 1200;

	const bannerReveal = {

		init: function() {
			this.cacheDOM();
			if (!this.section) {
				return;
			}
			if (this.prefersReducedMotion()) {
				return;
			}
			gsap.registerPlugin(ScrollTrigger, Observer, SplitText);
			ScrollTrigger.config({ignoreMobileResize: true});
			this.splitTitle();
			this.entranceAnimation();
			this.fillTitle();
			this.refreshOnFontLoad();
		},

		cacheDOM: function() {
			this.section = document.querySelector('.js-banner-reveal');
			if (!this.section) {
				return;
			}
			this.title = this.section.querySelector('.js-banner-reveal-title');
			this.description = this.section.querySelector('.js-banner-reveal-description');
		},

		prefersReducedMotion: function() {
			return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
		},

		// Letters, not words, are the unit that fills: with a four-word headline a
		// per-word stagger reads as one word lighting up, a pause, then the next.
		// Words are still split so nothing breaks mid-word, and aria: 'auto' puts
		// the whole headline on the h1 as its accessible name and hides the pieces,
		// so screen readers read the line instead of spelling it out.
		splitTitle: function() {
			if (!this.title) {
				return;
			}
			this.split = SplitText.create(this.title, {
				type: 'words,chars',
				wordsClass: 'banner-reveal__reveal-word',
				charsClass: 'banner-reveal__reveal-char',
				aria: 'auto',
			});
			this.chars = this.split.chars;
		},

		entranceAnimation: function() {
			if (this.title) {
				gsap.set(this.title, {opacity: 0, y: 24});
				gsap.to(this.title, {
					opacity: 1,
					y: 0,
					duration: 0.9,
					ease: 'power2.out',
					onComplete: () => gsap.set(this.title, {clearProps: 'willChange'}),
				});
			}

			if (this.description) {
				gsap.set(this.description, {opacity: 0, y: 16});
				gsap.to(this.description, {
					opacity: 1,
					y: 0,
					duration: 0.9,
					ease: 'power2.out',
					delay: 0.3,
					onComplete: () => gsap.set(this.description, {clearProps: 'willChange'}),
				});
			}
		},

		fillTitle: function() {
			if (!this.chars || !this.chars.length) {
				return;
			}

			const mm = gsap.matchMedia();

			mm.add({
				isPinned: `(min-width: ${PIN_MIN_WIDTH}px)`,
				isAuto: `(max-width: ${PIN_MIN_WIDTH - 1}px)`,
			}, (ctx) => {
				gsap.set(this.chars, {color: COLOR_PALE});

				// Width alone isn't enough — a short viewport on a wide screen
				// (tablet in landscape, laptop with a lot of browser chrome) leaves
				// the same band of empty page under a pinned banner. Uncommenting
				// the min-height in banner-reveal.scss makes this true at every
				// desktop height, which turns the pin back on everywhere.
				if (!ctx.conditions.isPinned || this.section.offsetHeight < window.innerHeight) {
					return this.fillOnEnter();
				}

				return this.fillWhilePinned();
			});
		},

		// The headline is set in a webfont — once it swaps in the section changes
		// height, which moves every trigger below it.
		refreshOnFontLoad: function() {
			if (document.fonts && document.fonts.ready) {
				document.fonts.ready.then(() => ScrollTrigger.refresh());
			}
		},

		// Phones and tablets: the banner is the first thing on the page, so
		// hijacking that first gesture reads as a broken page, and the section is
		// too short to pin without leaving empty page under it. The fill plays on
		// its own tempo instead — it can't be scrolled past because it isn't tied
		// to scroll at all.
		fillOnEnter: function() {
			gsap.to(this.chars, {
				color: COLOR_FILLED,
				ease: 'none',
				delay: 0.35,
				// Each letter takes `duration` to fill while the sweep only advances
				// by `amount / chars` between them, so a dozen letters are always
				// mid-fade and the reveal reads as one wash crossing the line. The
				// stagger's own ease eases that sweep in and out.
				stagger: {amount: 1.1, from: 'start', ease: 'power1.inOut'},
				duration: 0.5,
				scrollTrigger: {
					trigger: this.section,
					start: 'top bottom',
					once: true,
				},
			});

			return () => gsap.set(this.chars, {clearProps: 'color'});
		},

		fillWhilePinned: function() {
			const section = this.section;
			let armed = false;
			let target = 0;
			let observer = null;

			// Function declarations, not arrow consts: ScrollTrigger can fire its
			// toggle callbacks while it is still being created (the banner sits at
			// the top of the page, so it is already in view on load), which is
			// before the observer exists.
			function tick() {
				const current = window.scrollY;
				const diff = target - current;
				if (!diff) {
					return;
				}
				// deltaRatio keeps the speed limit in px/second rather than
				// px/frame, so 120Hz displays don't scroll twice as fast.
				const max = MAX_STEP * gsap.ticker.deltaRatio(60);
				window.scrollTo(0, current + Math.max(-max, Math.min(max, diff)));
			}

			function arm() {
				if (!observer || armed) {
					return;
				}
				armed = true;
				// Seed the queue with the momentum of the gesture that brought the
				// user here, so handing over to the limiter glides instead of
				// stopping dead at the section edge.
				const carry = tl.scrollTrigger.getVelocity() * ENTRY_MOMENTUM;
				target = window.scrollY + Math.max(-MAX_QUEUE, Math.min(MAX_QUEUE, carry));
				observer.enable();
				gsap.ticker.add(tick);
			}

			function disarm() {
				if (!observer || !armed) {
					return;
				}
				armed = false;
				observer.disable();
				gsap.ticker.remove(tick);
			}

			const tl = gsap.timeline({
				defaults: {ease: 'none'},
				scrollTrigger: {
					trigger: section,
					start: 'top top',
					// 70% of the fold. Percentages in a relative end are measured
					// against the viewport, and at the limiter's ceiling that works
					// out to a little over a second of held scrolling — enough for
					// the fill to read without the page feeling stuck. Note the
					// section has to cover the fold for this to look right (the
					// min-height in banner-reveal.scss) — otherwise the block below
					// creeps up under the pinned banner at the tail of the hold.
					end: '+=70%',
					pin: section,
					pinSpacing: true,
					// 1:1: the fill follows the scroll position exactly, and the
					// smoothing comes from the limiter instead. Scrub lag on top of
					// that reads as the letters lurching on after you stop.
					scrub: true,
					invalidateOnRefresh: true,
					onEnter: () => arm(),
					onEnterBack: () => arm(),
					onLeave: () => disarm(),
					onLeaveBack: () => disarm(),
				},
			});

			tl.to(this.chars, {
				color: COLOR_FILLED,
				// Both values are shares of the timeline: the sweep crosses the
				// headline in `amount` while each letter takes `duration` to fill,
				// so letters overlap heavily and the fill always finishes inside
				// FILL_RATIO regardless of how long the headline is.
				stagger: {amount: FILL_RATIO * 0.7, from: 'start'},
				duration: FILL_RATIO * 0.3,
			}, 0);

			// Idle hold: adds no animation, just consumes the rest of the pinned
			// scroll so the headline sits finished for a beat before it unpins.
			tl.to({}, {duration: 1 - FILL_RATIO}, FILL_RATIO);

			observer = Observer.create({
				type: 'wheel,touch',
				// Aligns wheel with touch: both then report a negative deltaY when
				// the user is moving further down the page.
				wheelSpeed: -1,
				preventDefault: true,
				// Observer batches its events into one call per frame, so deltaY
				// here is the whole gesture for this frame.
				onChangeY: (self) => {
					if (!armed) {
						return;
					}
					const current = window.scrollY;
					const next = target - self.deltaY;
					// Clamp against the live scroll position, not the old target,
					// so momentum can never bank more than MAX_QUEUE ahead.
					target = Math.max(current - MAX_QUEUE, Math.min(current + MAX_QUEUE, next));
				},
			});

			observer.disable();
			if (tl.scrollTrigger.isActive) {
				arm();
			}

			return () => {
				disarm();
				observer.kill();
				gsap.set(this.chars, {clearProps: 'color'});
			};
		},

	};

	bannerReveal.init();
});
